import { OAuth2 } from '@backstage/core-app-api';
import { CustomSignInPage } from './CustomSignInPage';
import {
  ApiBlueprint,
  AppRootElementBlueprint,
  configApiRef,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/frontend-plugin-api';
import { keycloakAuthApiRef } from '../apis';
import appPlugin from '@backstage/plugin-app';
import { UnifiedThemeProvider } from '@backstage/theme';
import { GlobalErrorHandler } from '../lib/GlobalErrorHandler';
import { RouteBodyAttributes } from '../lib/RouteBodyAttributes';
import { darkTheme, lightTheme } from '../theme/unifiedTheme';

const LightThemeIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none">
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M12 2.5v2.2M12 19.3v2.2M21.5 12h-2.2M4.7 12H2.5M18.7 5.3l-1.6 1.6M6.9 17.1l-1.6 1.6M18.7 18.7l-1.6-1.6M6.9 6.9 5.3 5.3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const DarkThemeIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none">
    <path
      d="M17.4 14.8A7.5 7.5 0 0 1 9.2 6.6a7.5 7.5 0 1 0 8.2 8.2Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

export const appModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ApiBlueprint.make({
      name: 'keycloak-auth-provider',
      params: defineParams =>
        defineParams({
          api: keycloakAuthApiRef,
          deps: {
            discoveryApi: discoveryApiRef,
            oauthRequestApi: oauthRequestApiRef,
            configApi: configApiRef,
          },
          factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
            OAuth2.create({
              discoveryApi,
              oauthRequestApi,
              configApi,
              provider: {
                id: 'keycloak',
                title: 'vwtech_ Cloud IDP',
                icon: () => null,
              },
              defaultScopes: ['openid', 'profile', 'email'],
              environment: configApi.getOptionalString('auth.environment'),
            }),
        }),
    }),
    appPlugin.getExtension('sign-in-page:app').override({
      params: {
        loader: async () => props =>
          (
            <CustomSignInPage
              {...props}
              providers={[
                {
                  id: 'keycloak-auth-provider',
                  title: 'Login with SSO',
                  message: 'Login with VW Group Cloud IDP',
                  apiRef: keycloakAuthApiRef,
                },
              ]}
            />
          ),
      },
    }),
    appPlugin.getExtension('theme:app/light').override({
      params: {
        theme: {
          id: 'light',
          title: 'Light Theme',
          variant: 'light',
          icon: <LightThemeIcon />,
          Provider: ({ children }) => (
            <UnifiedThemeProvider theme={lightTheme}>{children}</UnifiedThemeProvider>
          ),
        },
      },
    }),
    appPlugin.getExtension('theme:app/dark').override({
      params: {
        theme: {
          id: 'dark',
          title: 'Dark Theme',
          variant: 'dark',
          icon: <DarkThemeIcon />,
          Provider: ({ children }) => (
            <UnifiedThemeProvider theme={darkTheme}>{children}</UnifiedThemeProvider>
          ),
        },
      },
    }),
    AppRootElementBlueprint.make({
      name: 'global-error-handler',
      params: {
        element: (
          <>
            <RouteBodyAttributes />
            <GlobalErrorHandler />
          </>
        ),
      },
    }),
  ],
});
